import axios from './axiosConfig.js'
import { TarotReading } from '~/types/summaries.js'

const COMMUNITY_ENDPOINT = 'community/'

// ============ Types ============

export interface CommunityAuthor {
  uuid: string
  name: string
  username: string
  profilePic: string | null
  bio: string | null
  location: string | null
  isBookable?: boolean
}

export interface TopComment {
  id: string
  content: string
  author: CommunityAuthor
  likeCount: number
}

export interface CommunityPost {
  id: string
  authorId: string
  author: CommunityAuthor
  question: string
  reading: TarotReading
  deckName: string
  createdAt: string
  updatedAt: string
  _count?: { comments: number }
  hasCommented?: boolean
  topComment?: TopComment | null
}

export interface CommunityComment {
  id: string
  postId: string
  authorId: string
  author: CommunityAuthor
  content: string
  createdAt: string
  likeCount: number
  hasLiked: boolean
}

export interface CommunityPostWithComments extends CommunityPost {
  comments: CommunityComment[]
}

export interface PostsResponse {
  posts: CommunityPost[]
  pagination: {
    page: number
    totalPages: number
    total: number
  }
}

export interface Notification {
  id: string
  userId: string
  type: 'comment' | 'like' | 'thread_comment'
  postId: string | null
  commentId: string | null
  actorId: string | null
  actor: CommunityAuthor
  isRead: boolean
  createdAt: string
}

export interface NotificationsResponse {
  notifications: Notification[]
  unreadCount: number
  latestPostAt: string | null
}

export interface CreatePostInput {
  question: string
  reading: TarotReading
  deckName?: string
}

export interface UpdatePostInput {
  question?: string
  reading?: TarotReading
  deckName?: string
}

// ============ Post Cooldown ============

export interface PostCooldownStatus {
  canPost: boolean
  hoursRemaining: number
}

export const getPostCooldownStatus = async (): Promise<PostCooldownStatus> => {
  const response = await axios.get(`${COMMUNITY_ENDPOINT}can-post`)
  return response.data
}

// ============ Posts ============

export const getPosts = async (page: number = 1, filter?: string): Promise<PostsResponse> => {
  const response = await axios.get(`${COMMUNITY_ENDPOINT}posts`, {
    params: { page, filter }
  })
  return response.data
}

export const getPreviewPosts = async (): Promise<{ posts: CommunityPost[] }> => {
  const response = await axios.get(`${COMMUNITY_ENDPOINT}preview`)
  return response.data
}

export const getPost = async (postId: string): Promise<CommunityPostWithComments> => {
  const response = await axios.get(`${COMMUNITY_ENDPOINT}posts/${postId}`)
  return response.data
}

export const createPost = async (input: CreatePostInput): Promise<CommunityPost> => {
  const response = await axios.post(`${COMMUNITY_ENDPOINT}posts`, input)
  return response.data
}

export const updatePost = async (postId: string, input: UpdatePostInput): Promise<CommunityPost> => {
  const response = await axios.put(`${COMMUNITY_ENDPOINT}posts/${postId}`, input)
  return response.data
}

export const deletePost = async (postId: string): Promise<void> => {
  await axios.delete(`${COMMUNITY_ENDPOINT}posts/${postId}`)
}

// ============ Comments ============

export const createComment = async (postId: string, content: string): Promise<CommunityComment> => {
  const response = await axios.post(`${COMMUNITY_ENDPOINT}posts/${postId}/comments`, { content })
  return response.data
}

export const deleteComment = async (commentId: string): Promise<void> => {
  await axios.delete(`${COMMUNITY_ENDPOINT}comments/${commentId}`)
}

// ============ Likes ============

export const likeComment = async (commentId: string): Promise<void> => {
  await axios.post(`${COMMUNITY_ENDPOINT}comments/${commentId}/like`)
}

export const unlikeComment = async (commentId: string): Promise<void> => {
  await axios.delete(`${COMMUNITY_ENDPOINT}comments/${commentId}/like`)
}

// ============ Reports ============

export const reportContent = async (type: 'post' | 'comment', postId: string, commentId?: string): Promise<void> => {
  await axios.post(`${COMMUNITY_ENDPOINT}report`, { type, postId, commentId })
}

// ============ Notifications ============

export const getNotifications = async (): Promise<NotificationsResponse> => {
  const response = await axios.get(`${COMMUNITY_ENDPOINT}notifications`)
  return response.data
}

export const markNotificationsRead = async (notificationIds?: string[]): Promise<void> => {
  await axios.post(`${COMMUNITY_ENDPOINT}notifications/read`, { notificationIds })
}

// ============ Card Picker ============

export interface TarotCard {
  id: string
  slug: string
  name: string
  category: string
  order: number
  imagePath: string | null
  keywords?: string[]
  keywordsReverse?: string[]
}

export const getTarotCards = async (): Promise<TarotCard[]> => {
  const response = await axios.get('guidebook/cards')
  return response.data.cards
}
